// 열람 화면의 시퀀스 구독 — **절대 시각을 그리는 첫 자리**
//
// ── 구성 화면과 정반대다 ────────────────────────────────────────────────────
//
// 구성 화면은 시계를 한 번도 읽지 않는다. 거기서는 절대 시각이 아직 **존재하지 않기**
// 때문이다. 이 화면은 반대로 `impactMainAt` 앵커를 받아 자기 동기 시계 위에서 카운트다운을
// 센다. **서버는 틱을 보내지 않는다.** 앵커가 30밀리초 늦게 도착하면 카운트다운이 조금 늦게
// 그려질 뿐이지만, 틱을 받아 그리면 **틀리게** 그려진다 — 네트워크 지터가 그대로 시각이 된다.
//
// ── 왜 구성 쪽 저장소를 재사용하지 않는가 ───────────────────────────────────
//
// 계획서가 구성 화면 경로를 건드리지 말라고 못박았다. 재구성 갈래가 같은 창에서 병렬로
// 돌 수 있고 그 근거가 「두 화면이 공유하는 컴포넌트가 없다」이기 때문이다. **다만 전송
// 층은 나눠 쓴다** — `subscribeServerEvents` 가 토큰당 소켓 하나만 들고, 두 벌로 열면
// 서로를 쫓아낸다(2026-08-13에 그 일이 실제로 났다).
//
// ── 이름은 두 방송을 함께 들어야 낡지 않는다 ────────────────────────────────
//
// 명부가 바뀌면 서버는 **구성 중 시퀀스만** 다시 방송한다. 내보낸 시퀀스는 방송하지 않는데,
// 그것이 옳다 — 행군 값이 얼어붙는 것이 dispatch 의 계약이라 시퀀스는 안 움직여야 한다.
// 그런데 **이름은 얼어붙는 값이 아니다.** CT 가 개명하면 카드의 이름도 따라가야 하고, 그
// 신호는 `presence.updated` 로 온다(개명 라우트가 접속 명단을 다시 세운다).
//
// 그래서 이 파일은 방송 둘을 듣는다. 하나만 들으면 내보낸 뒤의 개명이 카드에서 사라지고,
// 같은 화면 위쪽의 큰 이름과 아래쪽 카드가 서로 다른 이름을 말한다. 접속 화면에서 똑같은
// 구멍을 2026-08-14에 메웠다 — 화면이 사실을 붙들지 않고 파생시키게 하는 것이 답이었다.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Rally, Roster, Sequence } from '../../../contract/schema.ts'
import { duration, instant, toMillis } from '../../../core/time/brand.ts'
import { solveOnImpactAxis, type RallyDraft, type RallyKind } from '../../../core/timing/attack.ts'
import { call, routes, subscribeServerEvents } from '../../access/client.ts'
import { accept, issue, newReadOrder, supersede } from '../../access/read-order.ts'

/**
 * 시퀀스 읽기가 다시 읽어 나을 이유로 실패했을 때(503, 망 끊김) 다시 읽는 횟수와 대기.
 *
 * 대기는 0.5~1.5초 사이에서 흩뜨린다. 재접속 폭풍에서 함께 실패한 화면들이 같은 순간에 다시
 * 몰리지 않게 하려는 것이다. 값은 세션 선택이고, 잠금 줄이 비는 데 걸리는 시간을 잰 근거는
 * 없다 (확인 안 함). 세 번이면 대기만 최대 4.5초이고, 읽기 한 번은 DB 연결 풀 대기 상한(5초)까지
 * 걸릴 수 있다.
 */
const SEQUENCE_RETRIES = 3
const SEQUENCE_RETRY_MIN_MS = 500
const SEQUENCE_RETRY_SPREAD_MS = 1_000

/**
 * 다시 읽기 전 대기. 0.5~1.5초 사이에서 흩뜨린다.
 *
 * 흩뜨림에 Math 객체의 예측 가능한 난수를 쓰지 않는다. 이 저장소는 제품 코드 어디에도 그것을 두지
 * 않는다는 규칙을 검사로 지킨다(`tests/unit/codes/codes.test.ts`, 문자열로 찾으므로 주석에도 그 이름을
 * 적지 않는다). 여기는 비밀을 만드는 자리가 아니지만, 예외를 하나 두면 그 검사가 막으려는 실수와
 * 구별되지 않는다.
 */
function retryDelayMs(): number {
  const [word = 0] = crypto.getRandomValues(new Uint32Array(1))
  return SEQUENCE_RETRY_MIN_MS + (word / 0x1_0000_0000) * SEQUENCE_RETRY_SPREAD_MS
}

/** 다시 읽어도 나을 수 있는 실패인가. 서버가 줄이 넘쳤다고 답했거나(503) 망이 끊겼다(0). */
function worthRetrying(status: number): boolean {
  return status === 503 || status === 0
}

/** 계약의 rally 를 계산 층이 받는 모양으로 옮긴다. 값을 바꾸지 않는다. */
function toDraft(line: Rally): RallyDraft {
  return {
    id: line.id,
    kind: line.type as RallyKind,
    rallying: duration(line.rallyingMs),
    marching: duration(line.marchingMs),
    offset: duration(line.offsetMs),
  }
}

/** 화면이 카드 한 장을 그리는 데 필요한 전부. 시각은 절대값이고 밀리초 정수다. */
/**
 * 명부에서 못 찾은 사람의 이름을 정한다. **빈 문자열을 「이름 없음」으로 쓰지 않는다.**
 *
 * 예전에는 못 찾으면 `''` 를 흘렸고, 바꿔치기는 **화면마다 한 벌씩** 있었다. 그 모양이
 * 위험한 이유는 `''` 가 타입상 완전한 이름이라서다 — `` `${name} 듣기` `` 같은 자리를
 * 아무 경고 없이 지나가고, 화면이 하나 늘 때마다 바꿔치기 한 줄을 빠뜨린다. 실제로 여섯
 * 자리 중 넷이 원본을 그대로 썼다: 듣기·따라가기 버튼의 낭독기 이름 둘, 상단 고정줄 요약,
 * 그리고 defense 탭으로 내보내는 값. 마지막 것이 **이름 없는 카운트다운**이 되어 2026-08-16
 * 대리 관측에 통과·불통 항목의 불통으로 잡혔다.
 *
 * 그래서 바꿔치기를 값이 **만들어지는 자리** 하나로 옮긴다. 이 아래로는 빈 이름이 흐르지
 * 않으므로 화면은 그것을 다룰 필요가 없고, 새 화면이 생겨도 빠뜨릴 줄 자체가 없다.
 */
export function displayRosterName(nickname: string | undefined, gone: string): string {
  return nickname === undefined || nickname === '' ? gone : nickname
}

export interface ViewRow {
  rally: Rally
  /**
   * 맡은 사람의 이름. 계약의 rally 에는 이름이 없고 id 만 있다.
   *
   * **언제나 사람이 읽을 수 있는 값이다** — 명부에서 내려간 사람은 대체 이름으로 채워져
   * 나온다. 빈 문자열은 여기까지 오지 않는다.
   */
  rosterName: string
  openMs: number
  impactMs: number
}

export interface ViewStore {
  /** 첫 응답이 왔는가. 껍데기와 값을 가르는 자리다. */
  loaded: boolean
  sequence: Sequence | null
  /** 내보내기 전에는 빈 배열이다 — 그릴 절대 시각이 아직 없다. */
  rows: readonly ViewRow[]
  connected: boolean
  /** 세션이 서버에 없다. 화면은 물러나 다시 들어올 길을 내준다. */
  sessionGone: boolean
}

/**
 * @param gone 명부에서 내려간 사람에게 붙일 대체 이름. **부르는 쪽이 준다** — 이 훅은
 *   i18n 을 모르고, 그래야 언어가 바뀌어도 이 파일이 흔들리지 않는다.
 */
export function useViewSequence(token: string | null, gone: string): ViewStore {
  const [sequence, setSequence] = useState<Sequence | null>(null)
  const [rosters, setRosters] = useState<readonly Roster[]>([])
  const [loaded, setLoaded] = useState(false)
  const [connected, setConnected] = useState(false)
  const [sessionGone, setSessionGone] = useState(false)

  // 콜백이 매 렌더마다 새로 만들어져 구독이 끊겼다 붙는 것을 막는다. 소켓이 재구독마다
  // 갈리면 그 순간의 방송을 놓치고, 놓친 화면에는 그럴듯한 옛 숫자가 남는다.
  const tokenRef = useRef(token)
  tokenRef.current = token

  // **늦게 온 옛 응답이 새 값을 덮지 않게 한다** (2026-09-28). 규칙과 까닭은
  // `src/web/access/read-order.ts` 가 진다. 시퀀스는 방송으로도 오므로 방송이 번호를 넘긴다.
  const rosterOrder = useRef(newReadOrder())
  const sequenceOrder = useRef(newReadOrder())
  // 효과가 돌 때마다 세대가 하나 오른다. 다시 읽기는 자기가 시작한 세대가 아직 지금 세대일
  // 때만 이어 간다. 효과가 걷혔다 다시 붙으면(개발 모드의 이중 마운트, 토큰이 바뀜) 앞 세대의
  // 다시 읽기가 새 세대의 값을 보고 되살아나지 않게 하려는 것이다.
  const generation = useRef(0)

  /**
   * 시퀀스를 읽는다. **503 이나 망 끊김으로 실패하면 세 번까지 다시 읽는다** (2026-09-28).
   *
   * 다시 붙은 직후의 읽기가 서버 잠금 줄이 넘쳐 503 을 받고, dispatch 방송은 이 화면이 붙기
   * 전에 이미 나갔다고 하자. 그러면 이 화면이 작전을 알 길이 다음 방송뿐이고, 그 사이 연맹원은
   * 아무 신호 없이 자기 차례를 놓친다. 재접속 폭풍 측정과 적대 검토가 그 길을 짚었다.
   *
   * 기다리는 사이 방송이나 다른 읽기가 더 새 값을 썼으면 멈춘다. 그러지 않으면 첫 로딩에서
   * 방송으로 이미 온 작전이 다시 읽기가 끝날 때까지 「불러오는 중」 뒤에 가려진다.
   *
   * 명부 응답을 기다리지 않고 제 응답이 오는 대로 쓴다. 둘을 묶어 늦게 쓰면, 그 사이에 온
   * dispatch 방송을 옛 시퀀스가 덮을 틈이 넓어진다.
   */
  const readSequence = useCallback(async (mine: number): Promise<void> => {
    let previous = 0
    for (let attempt = 0; attempt <= SEQUENCE_RETRIES; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs()))
        if (sequenceOrder.current.applied > previous) return
      }
      const current = tokenRef.current
      if (current === null || mine !== generation.current) return
      const ticket = issue(sequenceOrder.current)
      previous = ticket
      const result = await call<Sequence>(routes.sequence, { token: current })
      if (result.ok) {
        if (accept(sequenceOrder.current, ticket)) setSequence(result.data)
        return
      }
      if (!worthRetrying(result.status) || sequenceOrder.current.applied > ticket) return
    }
  }, [])

  const readRosters = useCallback(async (): Promise<void> => {
    const current = tokenRef.current
    if (current === null) return
    const ticket = issue(rosterOrder.current)
    const result = await call<{ rosters: Roster[] }>(routes.rosters, { token: current })
    if (result.ok && accept(rosterOrder.current, ticket)) setRosters(result.data.rosters)
  }, [])

  const readAll = useCallback(
    async (mine: number): Promise<void> => {
      if (tokenRef.current === null) return
      await Promise.all([readSequence(mine), readRosters()])
      setLoaded(true)
    },
    [readSequence, readRosters],
  )

  useEffect(() => {
    if (token === null) return
    generation.current += 1
    const mine = generation.current
    void readAll(mine)

    const unsubscribe = subscribeServerEvents(token, {
      onEvent: (message) => {
        if (message.kind === 'sequence.updated' && message['sequence'] !== undefined) {
          supersede(sequenceOrder.current)
          setSequence(message['sequence'] as Sequence)
          return
        }
        // **명부가 바뀌었다는 유일한 신호다** — 내보낸 시퀀스에는 `sequence.updated` 가
        // 오지 않으므로, 이것을 안 들으면 개명이 카드에 영영 닿지 않는다. 방송 본문에
        // 이름이 실려 있어도 목록을 다시 읽는다: 접속 명단은 **접속한 사람**만 담고
        // 카드에는 접속하지 않은 사람도 뜬다.
        if (message.kind === 'presence.updated') void readRosters()
      },
      onStatus: (status) => {
        setConnected(status.connected)
        setSessionGone(status.sessionGone)
        // **다시 붙을 때마다 통째로 다시 읽는다.** 끊겨 있던 동안 dispatch 가 지나갔으면
        // 이 화면은 작전이 없다고 믿은 채 조용히 앉아 있게 된다. 그 침묵이 이 제품에서
        // 가장 나쁜 실패다 — 아무 신호도 없이 자기 차례가 지나간다.
        if (status.connected) void readAll(mine)
      },
    })
    return () => {
      generation.current += 1
      unsubscribe()
    }
  }, [token, readAll, readRosters])

  const rows = useMemo<readonly ViewRow[]>(() => {
    if (sequence === null || sequence.impactMainAt === null) return []

    const nameOf = (rosterId: string): string =>
      displayRosterName(rosters.find((row) => row.id === rosterId)?.nickname, gone)

    try {
      const solved = solveOnImpactAxis(
        sequence.rallies.map(toDraft),
        instant(sequence.impactMainAt),
      )
      return solved.map((line, index) => {
        const rally = sequence.rallies[index]
        if (rally === undefined) throw new RangeError('풀이 결과가 원본과 길이가 다르다')
        return {
          rally,
          rosterName: nameOf(rally.rosterId),
          openMs: toMillis(line.open),
          impactMs: toMillis(line.impact),
        }
      })
    } catch {
      // **여기서 죽는 것은 CT 한 명이 아니라 연맹원 20~50명의 화면이다.** main 이 없는
      // 시퀀스가 내보내지면 착탄축 풀이가 예외를 던지는데, 그 상태는 서버가 두 겹으로
      // 막고 있다(상태의 정의와 내보내는 문). 그래도 여기서 한 번 더 받는 이유는, 막는
      // 층이 뚫렸을 때 그 사실이 **빈 목록**으로 드러나는 편이 흰 화면보다 낫기 때문이다.
      return []
    }
  }, [sequence, rosters, gone])

  return { loaded, sequence, rows, connected, sessionGone }
}
